import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { DiagnosticList } from '@/components/DiagnosticList/DiagnosticList';
import { Select } from '@/components/Select/Select';
import { TextArea } from '@/components/TextArea/TextArea';
import { TextField } from '@/components/TextField/TextField';
import type {
  Diagnostic,
  MessageTemplateScope,
  MessageTemplateStatus,
} from '@/graphql/message-templates';
import { SCOPE_LABELS } from '@/lib/scopes';

import styles from './TemplateEditor.module.css';

export interface TemplateDraft {
  scope: MessageTemplateScope;
  title: string;
  body: string;
  category: string;
  status: MessageTemplateStatus;
}

export interface TemplateEditorProps {
  mode: 'create' | 'edit';
  /** The scopes this token can write to. The two brokerage-facing ones need an
   *  organisation, which a Turnstile login resolves only when the person holds
   *  exactly one membership. */
  writableScopes: readonly MessageTemplateScope[];
  initial?: Partial<TemplateDraft>;
  busy?: boolean;
  /** The envelope's `errors`: what is wrong with the *body*. */
  errors?: readonly Diagnostic[];
  /** A refusal about the request rather than the body — no token principal,
   *  no organisation, a template that is not yours. */
  error?: string | null;
  onSubmit: (draft: TemplateDraft) => void;
  onCancel: () => void;
}

/**
 * Write a template, or mint the next version of one.
 *
 * The two modes are one form because they differ in exactly two arguments, and
 * both of those are about identity rather than content: `scope` and `category`
 * are set when a template is created and are not `editMessageTemplate`'s to
 * change. Type is not offered at all — it is fixed at creation, because
 * changing it would change what a body means without changing a character of
 * it, and there is one value today anyway.
 *
 * **Every save is a version.** Title and body both live on the version row, so
 * a retitle mints one exactly as a rewrite does; nothing here autosaves.
 */
export function TemplateEditor({
  mode,
  writableScopes,
  initial,
  busy = false,
  errors = [],
  error = null,
  onSubmit,
  onCancel,
}: TemplateEditorProps) {
  const [scope, setScope] = useState<MessageTemplateScope>(
    initial?.scope ?? writableScopes[0] ?? 'PERSON',
  );
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [status, setStatus] = useState<MessageTemplateStatus>(initial?.status ?? 'ACTIVE');

  const incomplete = title.trim() === '' || body.trim() === '';

  const heading = mode === 'create' ? 'New template' : 'Edit';

  return (
    <form
      // Named, because the filter bar has `Scope` and `Status` controls of its
      // own and identically labelled selects on one page are ambiguous to
      // anyone reaching them by name rather than by position.
      aria-label={heading}
      className={styles.editor}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ scope, title, body, category, status });
      }}
    >
      <h3 className={styles.heading}>{heading}</h3>

      {mode === 'create' ? (
        <>
          <Select
            disabled={writableScopes.length === 0}
            hint="Keystone takes a scope, never a destination id: the token already names one person and one brokerage."
            label="Scope"
            onChange={(value) => setScope(value as MessageTemplateScope)}
            options={writableScopes.map((value) => [value, SCOPE_LABELS[value]] as const)}
            value={scope}
          />
          <TextField
            hint="Free text, stored verbatim and compared exactly — Keystone has no vocabulary for these, so a typo makes a new group."
            label="Category"
            onChange={(event) => setCategory(event.target.value)}
            placeholder="chase"
            value={category}
          />
          <Select
            hint="A create defaults to ACTIVE — writing a template is deliberate, and one that landed invisible would have to be published before it appeared anywhere."
            label="Status"
            onChange={(value) => setStatus(value as MessageTemplateStatus)}
            options={[
              ['ACTIVE', 'Active — offered in the composer'],
              ['DRAFT', 'Draft — not offered'],
            ]}
            value={status}
          />
        </>
      ) : null}

      <TextField
        label="Title"
        onChange={(event) => setTitle(event.target.value)}
        placeholder="Chasing a missing document"
        value={title}
      />

      <TextArea
        face="mono"
        hint="Jinja. Keystone stores it verbatim and infers the variable paths from it; nothing here substitutes."
        label="Body"
        onChange={(event) => setBody(event.target.value)}
        placeholder="Hi {{ applicant.firstName }}, we still need your {{ document.label }}."
        rows={8}
        value={body}
      />

      {error ? (
        <Callout tone="danger" title="Keystone refused the request">
          {error}
        </Callout>
      ) : null}

      <DiagnosticList errors={errors} />

      <div className={styles.actions}>
        <Button onClick={onCancel} type="button" variant="ghost">
          Cancel
        </Button>
        <Button disabled={incomplete} loading={busy} type="submit">
          {mode === 'create' ? 'Create' : 'Save as a new version'}
        </Button>
      </div>
    </form>
  );
}
